import { Menu, Spin } from 'antd';
import { Dropdown, Icon } from 'Components';
import { usePermission } from 'Components/Permission/Permission.hooks';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, AxiosErrorResponse } from 'app-types';
import {
	ReportType,
	SavedReport,
	DownloadSavedReportRequestWithFileType
} from 'Services/Api/Reports/Types';
import { useModal } from 'Hooks/useModal';
import { useStores } from 'Hooks/useStore';
import { useHandleDownloadError } from 'Hooks';
import { handleAxiosError, getAxiosErrorType } from 'Helpers/api';
import { REPORT_TYPES_PDF_ONLY } from 'Pages/Report/EditReport/EditReport.constants';
import {
	ReportModalAction,
	ReportModalValue
} from '../ReportModals/ReportModals.types';
import { generatePath, useHistory } from 'react-router-dom';
import { getAppRoutes } from 'Pages/App/App.constants';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';

enum MenuKey {
	Preview = 'Preview',
	Edit = 'Edit',
	Download = 'Download',
	Clone = 'clone',
	Delete = 'Delete'
}

type DropdownMenuItem = MenuItem<MenuKey> & {
	hideForReportTypes?: ReportType[];
};

const MENU_ITEMS: DropdownMenuItem[] = [
	{
		key: MenuKey.Preview,
		title: 'report.preview',
		icon: <Icon type="eye" />,
		hideForReportTypes: REPORT_TYPES_PDF_ONLY
	},
	{
		key: MenuKey.Edit,
		title: 'edit',
		icon: <Icon type="edit" />
	},
	{
		key: MenuKey.Download,
		title: 'download',
		icon: <Icon type="envelope" />
	},
	{
		key: MenuKey.Clone,
		title: 'clone',
		icon: <Icon type="discount" />
	},
	{
		key: MenuKey.Delete,
		title: 'delete',
		icon: <Icon type="delete" />
	}
];

type ActionsButtonProps = {
	report: SavedReport;
	isMobile: boolean
};
const ActionsButton = ({ report, isMobile }: ActionsButtonProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const { reportStore, downloadReportsStore, authStore } = useStores();
	const { getHasPermission } = usePermission();
	const { showModal } = useModal();
	const { setToastInfo } = useDashboardContext();
	const handleDownloadError = useHandleDownloadError();
	const [loading, setLoading] = useState(false);

	const handlePreview = async () => {
		try {
			const data = await reportStore.previewReport({
				reportId: report.id
			});

			if (!data) return;

			const modalValue: ReportModalValue = {
				name: ReportModalAction.Preview,
				value: {
					data
				}
			};

			showModal(modalValue);
		} catch (e) {
			const axiosError = e as AxiosErrorResponse;
			const { isNotFoundError } = getAxiosErrorType(axiosError);
			if (authStore.isCurrentUserOwner && isNotFoundError) {
				if(setToastInfo) setToastInfo({ type: "warning", message: t('errorCodes.NO_AVAILABLE_PROPERTIES') })
			} else {
				handleAxiosError(axiosError, t);
			}
		}
	};

	const onMenuClick = async (info: { key: React.Key }) => {
		const menuKey = info.key as MenuKey;
		setLoading(true);
		switch (menuKey) {
			case MenuKey.Download:
				try {
					await downloadReportsStore.generateAndDownloadReport({
						reportId: report.id,
						fileType: report.fileFormat,
						reportType: report.reportType
					} as DownloadSavedReportRequestWithFileType);
				} catch (error) {
					const axiosError = error as AxiosErrorResponse;
					handleDownloadError(axiosError);
				}
				break;
			case MenuKey.Preview:
				await handlePreview();
				break;
			case MenuKey.Delete:
				const deleteValue: ReportModalValue = {
					name: ReportModalAction.Delete,
					value: report
				};

				showModal(deleteValue);
				break;
			case MenuKey.Clone:
				const cloneValue: ReportModalValue = {
					name: ReportModalAction.Clone,
					value: report
				};

				showModal(cloneValue);
				break;
			case MenuKey.Edit:
				history.push(
					generatePath(getAppRoutes().EDIT_REPORT, {
						reportId: report.id
					})
				);
				break;
		}
		setLoading(false);
	};

	const menuItems = useMemo(() => {
		return MENU_ITEMS.filter(({ hideForReportTypes, permission, key }) => {
			const hasPermission =
				!permission || getHasPermission(permission).permitted;

			return (
				hasPermission &&
				!hideForReportTypes?.includes(report.reportType) && (isMobile ? key !== MenuKey.Preview : true)
			);
		});
	}, [getHasPermission, report.reportType, isMobile]);

	if (!menuItems.length) return null;

	return (
		<Dropdown overlayClassName='saved-menu-options'
			overlay={
				<Menu onClick={onMenuClick}>
					{menuItems.map((menuItem) => <Menu.Item key={menuItem.key} className={menuItem.title ==="delete" ? 'delete-btn' : ""}>{t(menuItem.title)}</Menu.Item>)}
				</Menu>
			}
			arrow={false}
		>
			{loading ? <Spin spinning={loading} /> : <Icon type="optionIcon" />}
		</Dropdown>
	);
};

export default ActionsButton;
